<script lang="ts">
  import { opdsStore } from '../stores/opds.svelte';
  import type { OPDSEntry } from '@legere/opds-client';
  import OPDSSearchBar from './OPDSSearchBar.svelte';
  import OPDSBreadcrumbs from './OPDSBreadcrumbs.svelte';
  import OPDSFacets from './OPDSFacets.svelte';
  import OPDSErrorAlert from './OPDSErrorAlert.svelte';

  const { currentFeed, navigationHistory, loading, error } = $derived(opdsStore);

  let downloadingEntry = $state<string | null>(null);
  let downloadError = $state<string | null>(null);

  async function navigateToLink(href: string, title: string) {
    try {
      await opdsStore.fetchFeed(href, title);
    } catch (err) {
      console.error('Failed to navigate:', err);
    }
  }

  async function downloadBook(entry: OPDSEntry) {
    downloadingEntry = entry.id;
    downloadError = null;

    try {
      // Download book from OPDS
      const download = await opdsStore.downloadBook(entry);

      if (!download) {
        throw new Error('No downloadable content found');
      }

      const { blob, filename } = download;

      // Download to user's device
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      alert(`Book "${entry.title}" downloaded! You can now upload it to your library using the "Upload Book" button.`);
    } catch (err) {
      downloadError = err instanceof Error ? err.message : 'Failed to download book';
      console.error('Download error:', err);
    } finally {
      downloadingEntry = null;
    }
  }

  function getEntryLinks(entry: OPDSEntry) {
    const links = {
      navigation: entry.links.find((link) => {
        // Check for navigation/subsection rel
        if (link.rel === 'subsection' || link.rel === 'http://opds-spec.org/facet') {
          return true;
        }

        // Check for OPDS catalog type
        if (link.type?.includes('application/atom+xml')) {
          // Make sure it's NOT an acquisition link
          return !link.rel?.includes('opds-spec.org/acquisition');
        }

        return false;
      }),
      acquisition: entry.links.find(
        (link) =>
          link.rel?.includes('opds-spec.org/acquisition') ||
          link.rel === 'http://opds-spec.org/acquisition' ||
          link.rel === 'http://opds-spec.org/acquisition/open-access' ||
          link.rel === 'http://opds-spec.org/acquisition/buy' ||
          link.rel === 'http://opds-spec.org/acquisition/borrow' ||
          link.rel === 'http://opds-spec.org/acquisition/sample'
      ),
      cover: entry.links.find(
        (link) =>
          link.rel === 'http://opds-spec.org/image' ||
          link.rel === 'http://opds-spec.org/image/thumbnail' ||
          link.rel === 'http://opds-spec.org/cover'
      ),
    };

    // Debug log for navigation issues
    if (entry.links.length > 0 && !links.navigation && !links.acquisition) {
      console.log('Entry with unrecognized links:', {
        title: entry.title,
        links: entry.links.map(l => ({ rel: l.rel, type: l.type, href: l.href }))
      });
    }

    return links;
  }

  function formatAuthors(entry: OPDSEntry): string {
    if (!entry.author || entry.author.length === 0) return 'Unknown Author';
    return entry.author.map((a) => a.name).join(', ');
  }
</script>

<div class="opds-feed-browser">
  <!-- Breadcrumbs Navigation -->
  <OPDSBreadcrumbs />

  <!-- Search Bar (if search is available) -->
  {#if currentFeed?.searchLink}
    <OPDSSearchBar />
  {/if}

  <!-- Facets (if available) -->
  <OPDSFacets />

  <!-- Loading state -->
  {#if loading}
    <div class="loading">
      <div class="spinner"></div>
      <p>Loading catalog...</p>
    </div>
  {/if}

  <!-- Error state -->
  {#if error}
    <OPDSErrorAlert error={error} onDismiss={() => (opdsStore.error = null)} />
  {/if}

  <!-- Download error -->
  {#if downloadError}
    <div class="error-banner">
      <strong>Download Error:</strong> {downloadError}
      <button on:click={() => (downloadError = null)}>Dismiss</button>
    </div>
  {/if}

  <!-- Feed content -->
  {#if currentFeed && !loading}
    <div class="feed-content">
      <h1>{currentFeed.title}</h1>

      {#if currentFeed.entries.length === 0}
        <p class="empty-state">No entries in this catalog.</p>
      {:else}
        <div class="entries-grid">
          {#each currentFeed.entries as entry (entry.id)}
            {@const links = getEntryLinks(entry)}
            <div class="entry-card">
              {#if links.cover}
                <img
                  src={links.cover.href}
                  alt={entry.title}
                  class="entry-cover"
                  loading="lazy"
                />
              {:else}
                <div class="entry-cover-placeholder">📖</div>
              {/if}

              <div class="entry-details">
                <h3>{entry.title}</h3>
                <p class="entry-author">{formatAuthors(entry)}</p>

                {#if entry.summary}
                  <p class="entry-summary">{entry.summary}</p>
                {/if}

                {#if entry.dcterms?.publisher}
                  <p class="entry-meta">Publisher: {entry.dcterms.publisher}</p>
                {/if}

                {#if entry.dcterms?.issued}
                  <p class="entry-meta">Published: {entry.dcterms.issued}</p>
                {/if}

                {#if entry.category && entry.category.length > 0}
                  <div class="entry-tags">
                    {#each entry.category as cat}
                      <span class="tag">{cat.label || cat.term}</span>
                    {/each}
                  </div>
                {/if}

                <div class="entry-actions">
                  {#if links.navigation}
                    <button
                      class="btn-primary"
                      on:click={() => navigateToLink(links.navigation!.href, entry.title)}
                    >
                      Browse →
                    </button>
                  {/if}

                  {#if links.acquisition}
                    <button
                      class="btn-download"
                      on:click={() => downloadBook(entry)}
                      disabled={downloadingEntry === entry.id}
                    >
                      {#if downloadingEntry === entry.id}
                        Downloading...
                      {:else}
                        ⬇ Download
                      {/if}
                    </button>
                  {/if}
                </div>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .opds-feed-browser {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    color: #6b7280;
  }

  :global(.dark) .loading {
    color: #9ca3af;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e5e7eb;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  :global(.dark) .spinner {
    border-color: #374151;
    border-top-color: #60a5fa;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .error-banner {
    background: #fee2e2;
    color: #991b1b;
    padding: 1rem;
    margin: 1rem;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  :global(.dark) .error-banner {
    background: #7f1d1d;
    color: #fecaca;
  }

  .error-banner button {
    background: none;
    border: 1px solid currentColor;
    color: inherit;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
  }

  .error-banner button:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  :global(.dark) .error-banner button:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  .feed-content {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    background: #ffffff;
  }

  :global(.dark) .feed-content {
    background: #111827;
  }

  .feed-content h1 {
    font-size: 1.75rem;
    margin-bottom: 1.5rem;
    color: #1f2937;
  }

  :global(.dark) .feed-content h1 {
    color: #f9fafb;
  }

  .empty-state {
    text-align: center;
    color: #6b7280;
    padding: 3rem;
    font-style: italic;
  }

  :global(.dark) .empty-state {
    color: #9ca3af;
  }

  .entries-grid {
    display: grid;
    gap: 1rem;
  }

  .entry-card {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
  }

  :global(.dark) .entry-card {
    background: #1f2937;
    border-color: #374151;
  }

  .entry-cover,
  .entry-cover-placeholder {
    width: 100px;
    height: 150px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .entry-cover-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    font-size: 2rem;
  }

  :global(.dark) .entry-cover-placeholder {
    background: #374151;
  }

  .entry-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .entry-details h3 {
    font-size: 1.25rem;
    margin: 0;
    color: #1f2937;
  }

  :global(.dark) .entry-details h3 {
    color: #f9fafb;
  }

  .entry-author {
    color: #6b7280;
    font-weight: 500;
    margin: 0;
  }

  :global(.dark) .entry-author {
    color: #9ca3af;
  }

  .entry-summary {
    color: #374151;
    line-height: 1.4;
    margin: 0;
  }

  :global(.dark) .entry-summary {
    color: #d1d5db;
  }

  .entry-meta {
    color: #9ca3af;
    font-size: 0.9rem;
    margin: 0;
  }

  :global(.dark) .entry-meta {
    color: #6b7280;
  }

  .entry-tags {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .tag {
    padding: 0.25rem 0.5rem;
    background: #e5e7eb;
    border-radius: 4px;
    font-size: 0.85rem;
    color: #374151;
  }

  :global(.dark) .tag {
    background: #374151;
    color: #d1d5db;
  }

  .entry-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: auto;
  }

  .btn-primary,
  .btn-download {
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
  }

  .btn-primary {
    background: #3b82f6;
    color: white;
  }

  .btn-primary:hover {
    background: #2563eb;
  }

  .btn-download {
    background: #10b981;
    color: white;
  }

  .btn-download:hover:not(:disabled) {
    background: #059669;
  }

  .btn-download:disabled {
    background: #6b7280;
    cursor: not-allowed;
  }

  :global(.dark) .btn-download:disabled {
    background: #4b5563;
  }

  @media (max-width: 768px) {
    .entry-card {
      flex-direction: column;
    }

    .entry-cover,
    .entry-cover-placeholder {
      width: 100%;
      height: 200px;
    }
  }
</style>
